import type { VesselState, VesselType } from "@radar-sim/schema";

/**
 * Per-vessel-type identification helpers used by the target popover
 * and the night-mode renderer:
 *
 *   - **Day shape** (COLREG Rule 23–30): the matt-black geometric
 *     shape a vessel hoists by day to declare a status. Most vessels
 *     underway by day show no shape; fishing, RAM, NUC, anchor and a
 *     few others do.
 *   - **Light arrangement** (COLREG Rule 23–30): the navigation lights
 *     a vessel shows by night. Visible only in their declared arcs
 *     from forward, abeam, or astern.
 *
 * v1 covers the common cases for the vessel types we model. We don't
 * track tow-state or anchor-state yet, so e.g. a tug shows the
 * standard power-driven underway pattern by default rather than the
 * towing pattern; an anchored ferry would show the underway pattern
 * etc. Authors who need an explicit state can extend the schema in a
 * later phase.
 */

export type DayShape =
  /** Two cones apex-to-apex (a "basket"). Vessel engaged in fishing. */
  | "two-cones-apex-to-apex"
  /** Single black ball forward — vessel at anchor. */
  | "ball"
  /** Two black balls vertical — not under command. */
  | "two-balls"
  /** Three black balls vertical — vessel aground. */
  | "three-balls"
  /** Ball–diamond–ball — vessel restricted in maneuverability. */
  | "ball-diamond-ball"
  /** Diamond — towing operation > 200m. */
  | "diamond"
  /** Single cone point-down — sailing vessel proceeding under
   *  sail and power. */
  | "cone-down"
  /** Black cylinder — vessel constrained by her draught (Rule 28). */
  | "cylinder";

export type Light =
  | "port"      // red sidelight, port arc (247.5°–360° relative)
  | "starboard" // green sidelight, starboard arc (0°–112.5°)
  | "stern"     // white sternlight (112.5°–247.5°)
  | "masthead"  // white masthead, 225° forward arc
  | "all-round-white"      // anchor light, full 360°
  | "all-round-red-top"    // top of vertical red column (NUC, RAM, CBD)
  | "all-round-red-mid"    // middle of vertical red column
  | "all-round-red-bottom" // bottom of vertical red column (CBD only — 3 reds)
  | "all-round-white-mid"  // RAM mid white (red-white-red)
  | "fishing-green"        // green over white (trawler) — top
  | "fishing-white"        // green over white (trawler) — bottom
  | "yellow-flash"         // HSC all-round flashing yellow (Rule 23(b))
  | "pilot-white-top"      // pilot vessel: white over red — top
  | "pilot-red-bottom"     // pilot vessel: white over red — bottom
  | "tow-yellow"           // towing astern: yellow over sternlight
  // Dredger (Rule 27(d)) — RAM lights plus side-specific markers
  | "dredger-obstructed-top"    // 2 all-round reds on the obstructed side
  | "dredger-obstructed-bottom"
  | "dredger-clear-top"         // 2 all-round greens on the clear side
  | "dredger-clear-bottom"
  // Minesweeping (Rule 27(f)) — three all-round greens, one masthead +
  // one at each end of the fore yard. Triangular arrangement.
  | "minesweep-mast"
  | "minesweep-port-yard"
  | "minesweep-stbd-yard";

export interface LightArrangement {
  lights: ReadonlyArray<Light>;
  /** Plain-English description shown in the popover. */
  description: string;
}

/**
 * Day shape mapping. Looks at vessel state first (anchored, fishing,
 * etc.), then falls back to type-only defaults. Returns undefined for
 * vessels that show no shape (most commercial traffic underway).
 */
export function dayShapeFor(
  type: VesselType,
  state: VesselState | undefined = "underway",
): DayShape | undefined {
  // State takes precedence — that's the COLREG signal.
  switch (state) {
    case "anchored":
      return "ball";
    case "not-under-command":
      return "two-balls";
    case "restricted-in-maneuverability":
      return "ball-diamond-ball";
    case "constrained-by-draught":
      return "cylinder";
    case "towing-astern":
      // No day shape unless the tow is > 200m. Authors who model a
      // long tow should set state: "towing-long" instead.
      return undefined;
    case "towing-long":
      return "diamond";
    case "towing-alongside":
      // No specific day shape under Rule 24(c) for alongside towing.
      return undefined;
    case "dredging":
      // Rule 27(d) day shapes: ball-diamond-ball overall, plus 2
      // balls on obstructed side and 2 diamonds on clear side. We
      // surface the central RAM-style day shape; the side markers
      // would need their own positioning support to render properly.
      return "ball-diamond-ball";
    case "minesweeping":
      // Rule 27(f): three balls — one masthead + one at each end of
      // the fore yard. Closest existing shape is "three-balls"
      // (vertical), which is also used for aground. Renderer
      // distinguishes via the description.
      return "three-balls";
    case "aground":
      return "three-balls";
    case "sail-and-power":
      return "cone-down";
    case "fishing":
      return "two-cones-apex-to-apex";
    case "underway":
    case undefined:
      break;
  }
  // Type-only defaults: vessels whose type implies a default state.
  if (type === "fishing") return "two-cones-apex-to-apex";
  if (type === "dredger") return "ball-diamond-ball";
  if (type === "minesweeper") return "three-balls";
  return undefined;
}

export function describeDayShape(shape: DayShape): string {
  switch (shape) {
    case "two-cones-apex-to-apex":
      return "Two cones apex-to-apex (vessel engaged in fishing — Rule 26)";
    case "ball":
      return "Black ball (vessel at anchor — Rule 30)";
    case "two-balls":
      return "Two black balls (not under command — Rule 27)";
    case "three-balls":
      return "Three black balls vertical (vessel aground — Rule 30)";
    case "ball-diamond-ball":
      return "Ball-diamond-ball (restricted in maneuverability — Rule 27)";
    case "diamond":
      return "Diamond (towing astern > 200m — Rule 24)";
    case "cone-down":
      return "Cone point-down (sailing vessel under sail and power — Rule 25)";
    case "cylinder":
      return "Black cylinder (vessel constrained by her draught — Rule 28)";
  }
}

/**
 * Light arrangement for a vessel — driven by state first, then type.
 *
 * - State `anchored`: only an all-round white anchor light (small craft)
 *   or two for ≥50m. We model the 1-light case for v1.
 * - State `not-under-command`: 2 all-round red vertical, plus sidelights
 *   + stern when making way.
 * - State `restricted-in-maneuverability`: 3 all-round red-white-red
 *   vertical, plus normal lights when underway.
 * - State `constrained-by-draught`: 3 all-round red vertical (in addition
 *   to the standard underway lights).
 * - State `towing-long`: standard underway + extra masthead + yellow
 *   towing light over the stern.
 * - State `aground`: 2 all-round red + 2 all-round white (anchor lights).
 * - State `sail-and-power`: power-driven lights (sail-only would drop
 *   masthead but here we're under power too).
 * - State `fishing`: fishing-green over fishing-white + sidelights and
 *   stern when making way.
 *
 * Type-only defaults apply for any state-less vessel.
 */
export function lightsFor(
  type: VesselType,
  state: VesselState | undefined = "underway",
): LightArrangement | undefined {
  switch (state) {
    case "anchored":
      return {
        lights: ["all-round-white"],
        description:
          "Vessel at anchor — single all-round white forward (or two for ≥50m), no sidelights (Rule 30)",
      };
    case "not-under-command":
      return {
        lights: [
          "all-round-red-top",
          "all-round-red-mid",
          "port",
          "starboard",
          "stern",
        ],
        description:
          "Not under command — two all-round red lights vertical; sidelights and stern only when making way (Rule 27)",
      };
    case "restricted-in-maneuverability":
      return {
        lights: [
          "all-round-red-top",
          "all-round-white-mid",
          "all-round-red-mid",
          "port",
          "starboard",
          "stern",
          "masthead",
        ],
        description:
          "Restricted in ability to maneuver — red/white/red all-round vertical, plus normal underway lights (Rule 27)",
      };
    case "constrained-by-draught":
      return {
        lights: [
          "all-round-red-top",
          "all-round-red-mid",
          "all-round-red-bottom",
          "port",
          "starboard",
          "stern",
          "masthead",
        ],
        description:
          "Constrained by her draught — three all-round red lights vertical, plus normal underway lights (Rule 28)",
      };
    case "towing-astern":
      return {
        lights: ["port", "starboard", "stern", "masthead", "tow-yellow"],
        description:
          "Towing astern (tow ≤ 200m) — sidelights, two masthead lights vertical forward, sternlight, plus a yellow towing light above the stern (Rule 24(a))",
      };
    case "towing-long":
      return {
        lights: ["port", "starboard", "stern", "masthead", "tow-yellow"],
        description:
          "Towing astern > 200m — sidelights, three masthead lights forward in vertical line, sternlight, yellow towing light, and the towed vessel shows the same plus a diamond by day (Rule 24(a))",
      };
    case "towing-alongside":
      return {
        lights: ["port", "starboard", "stern", "masthead"],
        description:
          "Towing alongside or pushing ahead — two masthead lights forward in vertical line, sidelights, and two stern lights (the second over the first) (Rule 24(c))",
      };
    case "dredging":
      return {
        lights: [
          "all-round-red-top",
          "all-round-white-mid",
          "all-round-red-mid",
          "dredger-obstructed-top",
          "dredger-obstructed-bottom",
          "dredger-clear-top",
          "dredger-clear-bottom",
          "port",
          "starboard",
          "stern",
          "masthead",
        ],
        description:
          "Dredging or underwater operations — RAM lights (red/white/red) plus two all-round reds on the obstructed side and two all-round greens on the clear side. Pass on the green side only (Rule 27(d))",
      };
    case "minesweeping":
      return {
        lights: [
          "minesweep-mast",
          "minesweep-port-yard",
          "minesweep-stbd-yard",
          "port",
          "starboard",
          "stern",
          "masthead",
        ],
        description:
          "Minesweeping — three all-round green lights (one at masthead + one at each end of the fore yard) plus normal underway lights. Keep 1000m astern / 500m on either beam clear (Rule 27(f))",
      };
    case "aground":
      return {
        lights: [
          "all-round-red-top",
          "all-round-red-mid",
          "all-round-white",
        ],
        description:
          "Vessel aground — two all-round red over the anchor light(s) (Rule 30)",
      };
    case "sail-and-power":
    case "underway":
    case undefined:
      // Fall through to type-based defaults.
      break;
    case "fishing":
      return {
        lights: ["fishing-green", "fishing-white", "port", "starboard", "stern"],
        description:
          "Trawling — green over white all-round, plus sidelights and sternlight when making way (Rule 26)",
      };
  }

  // Type-based defaults (state is "underway" or absent).
  switch (type) {
    case "container":
    case "ferry":
    case "naval":
      return {
        lights: ["port", "starboard", "stern", "masthead"],
        description:
          "Power-driven vessel underway — red port + green starboard sidelights, white masthead forward, white sternlight aft (Rule 23)",
      };
    case "yacht-power":
    case "rib":
      return {
        lights: ["port", "starboard", "stern", "masthead"],
        description:
          "Power-driven vessel underway < 50m — red/green sidelights, masthead, sternlight (Rule 23)",
      };
    case "yacht-sail":
      return {
        lights: ["port", "starboard", "stern"],
        description:
          "Sailing vessel underway — red/green sidelights and white sternlight, no masthead (Rule 25)",
      };
    case "fishing":
      return {
        lights: ["fishing-green", "fishing-white", "port", "starboard", "stern"],
        description:
          "Trawling — green over white all-round, plus sidelights and sternlight when making way (Rule 26)",
      };
    case "tug":
      return {
        lights: ["port", "starboard", "stern", "masthead"],
        description:
          "Power-driven vessel underway — tow-pattern lights apply only when actually towing (set state: \"towing-long\" to show those)",
      };
    case "fastcat":
    case "hovercraft":
      return {
        lights: [
          "port",
          "starboard",
          "stern",
          "masthead",
          "yellow-flash",
        ],
        description:
          "High-speed craft — power-driven lights plus an all-round yellow flashing light visible 360° (Rule 23(b))",
      };
    case "pilot":
      return {
        lights: [
          "pilot-white-top",
          "pilot-red-bottom",
          "port",
          "starboard",
          "stern",
        ],
        description:
          "Pilot vessel on duty — all-round white over red at the masthead, plus sidelights and sternlight when making way (Rule 29)",
      };
    case "dredger":
      // Dredger underway-but-not-dredging: standard power-driven
      // lights. The RAM/obstructed/clear pattern only applies when
      // state is set to "dredging".
      return {
        lights: ["port", "starboard", "stern", "masthead"],
        description:
          "Dredger transiting — power-driven vessel underway lights. While actually dredging, set state to \"dredging\" for the Rule 27(d) pattern.",
      };
    case "minesweeper":
      // Minesweeper underway-not-operating: standard power-driven.
      // The three-greens pattern only applies when state is set to
      // "minesweeping".
      return {
        lights: ["port", "starboard", "stern", "masthead"],
        description:
          "Minesweeper transiting — power-driven vessel underway lights. While operating, set state to \"minesweeping\" for the Rule 27(f) pattern.",
      };
    case "buoy":
      return undefined;
  }
}

/**
 * Visibility arcs (relative bearing from the vessel's bow, where 0°
 * is dead ahead, 90° is starboard beam, 180° is astern, 270° is port
 * beam). Numbers come straight from COLREG Annex I.
 *
 * Returns true if the light is visible at this relative bearing.
 */
export function lightVisible(
  light: Light,
  relativeBearingFromTarget: number,
): boolean {
  const rb = ((relativeBearingFromTarget % 360) + 360) % 360;
  switch (light) {
    case "starboard":
      // Dead ahead (0°) to 22.5° abaft starboard beam (112.5°).
      return rb <= 112.5;
    case "port":
      // Dead ahead (0°/360°) to 22.5° abaft port beam (247.5°). The
      // arc wraps through the bow, so we include the rb=0 boundary
      // explicitly — a vessel dead ahead shows both sidelights.
      return rb >= 247.5 || rb < 0.001;
    case "stern":
      // 67.5° each side of dead astern.
      return rb >= 112.5 && rb <= 247.5;
    case "masthead":
      // Sidelights merged forward = 225° arc forward.
      return rb <= 112.5 || rb >= 247.5;
    case "all-round-white":
    case "all-round-red-top":
    case "all-round-red-mid":
    case "all-round-red-bottom":
    case "all-round-white-mid":
    case "fishing-green":
    case "fishing-white":
    case "yellow-flash":
    case "pilot-white-top":
    case "pilot-red-bottom":
    case "tow-yellow":
    case "dredger-obstructed-top":
    case "dredger-obstructed-bottom":
    case "dredger-clear-top":
    case "dredger-clear-bottom":
    case "minesweep-mast":
    case "minesweep-port-yard":
    case "minesweep-stbd-yard":
      return true;
  }
}

/**
 * CSS colour for each light. Used by the renderer and the popover
 * legend.
 */
export function colourFor(light: Light): string {
  switch (light) {
    case "port":
      return "#ef4444";
    case "starboard":
      return "#22c55e";
    case "stern":
    case "masthead":
    case "all-round-white":
    case "all-round-white-mid":
    case "fishing-white":
      return "#fef3c7";
    case "all-round-red-top":
    case "all-round-red-mid":
    case "all-round-red-bottom":
    case "pilot-red-bottom":
    case "dredger-obstructed-top":
    case "dredger-obstructed-bottom":
      return "#ef4444";
    case "fishing-green":
    case "dredger-clear-top":
    case "dredger-clear-bottom":
    case "minesweep-mast":
    case "minesweep-port-yard":
    case "minesweep-stbd-yard":
      return "#22c55e";
    case "yellow-flash":
    case "tow-yellow":
      return "#facc15";
    case "pilot-white-top":
      return "#fef3c7";
  }
}

/**
 * Some lights flash rather than burn steady — render-side hint so the
 * draw layer can pulse them at the COLREG rate. Steady lights return
 * `undefined`. v1 hard-codes the typical rates; later we could move
 * these to the schema.
 */
export function flashRateHz(light: Light): number | undefined {
  switch (light) {
    case "yellow-flash":
      return 1.5; // 90 fpm — high-speed-craft yellow
    default:
      return undefined;
  }
}

/**
 * Sound signal a vessel must make in restricted visibility (Rule 35).
 * Returns both the chart abbreviation and the long-form rule
 * reference so students see both forms next to each other in the
 * popover. Returns undefined for unrecognised type/state combos.
 *
 * Notation:
 *   "—"  long blast (~4 to 6 seconds)
 *   "·"  short blast (~1 second)
 */
export interface FogSignal {
  /** Compact chart-style notation (e.g. "—" or "— · ·"). */
  pattern: string;
  /** Plain-English description with the rule reference. */
  description: string;
}

export function fogSignalFor(
  type: VesselType,
  state: VesselState | undefined = "underway",
): FogSignal | undefined {
  switch (state) {
    case "anchored":
      return {
        pattern: "Bell 5s, every 1 min",
        description:
          "Bell rung rapidly for ~5 seconds at intervals of not more than 1 minute (Rule 35(g)). Vessels ≥100m: bell forward + gong aft, plus 3 distinct strokes of the bell beforehand to warn approaching traffic.",
      };
    case "aground":
      return {
        pattern: "3 strokes · Bell · 3 strokes",
        description:
          "Three distinct strokes of the bell, the rapid 5-second ring of the bell, then three further distinct strokes — at intervals of not more than 1 minute (Rule 35(h)).",
      };
    case "fishing":
    case "not-under-command":
    case "restricted-in-maneuverability":
    case "constrained-by-draught":
    case "towing-astern":
    case "towing-long":
    case "towing-alongside":
    case "dredging":
    case "minesweeping":
      return {
        pattern: "— · ·",
        description:
          "One prolonged blast followed by two short blasts at intervals of not more than 2 minutes (Rule 35(c)). Used by vessels not under command, restricted in their ability to maneuver (including dredging and minesweeping), constrained by draught, fishing, sailing, and tugs while towing.",
      };
    case "sail-and-power":
    case "underway":
    case undefined:
      break;
  }
  // Type-based defaults for an underway vessel making way.
  switch (type) {
    case "yacht-sail":
      return {
        pattern: "— · ·",
        description:
          "Sailing vessel — one prolonged blast plus two short blasts at intervals of not more than 2 minutes (Rule 35(c)).",
      };
    case "fishing":
      return {
        pattern: "— · ·",
        description:
          "Vessel engaged in fishing — one prolonged plus two short blasts every 2 minutes (Rule 35(c)).",
      };
    case "pilot":
      return {
        pattern: "— or — · · — (4 short)",
        description:
          "Pilot vessel on duty — sounds the appropriate signal for being underway or at anchor, plus an identity signal of four short blasts (Rule 35(j)).",
      };
    case "container":
    case "ferry":
    case "tug":
    case "naval":
    case "yacht-power":
    case "rib":
    case "fastcat":
    case "hovercraft":
    case "dredger":
    case "minesweeper":
      return {
        pattern: "—",
        description:
          "Power-driven vessel underway making way — one prolonged blast at intervals of not more than 2 minutes (Rule 35(a)). When stopped and making no way: two prolonged blasts.",
      };
    case "buoy":
      return undefined;
  }
}
